import type { SupabaseClient } from '../client'
import { SupabaseError } from '../errors'

/**
 * O Progresso do cliente (direção C refinada, 22/10/2026).
 *
 * O portal lê só o que a equipe PUBLICOU: a fotografia montada no banco por
 * operacoes.publicar_progresso. Os pedidos ao cliente são vivos (saem na hora)
 * e a resposta passa por RPC. Plano e contrato:
 * apps/portal/docs/design/progresso-acompanhamento/PLANO.md
 */

export type HistoricoDePrazo = {
  de: string | null
  para: string | null
  motivo: string
  em: string
}

export type DemandaPublicada = {
  id: number
  titulo: string
  texto: string | null
  /** status do quadro: todo, backlog, progress, review, qa, done. */
  andamento: string
  prazo: string | null
  entregue_em: string | null
  historico: HistoricoDePrazo[]
}

export type BlocoPublicado = {
  id: number
  titulo: string
  texto: string | null
  /** planned, active ou done. */
  andamento: string
  prazo: string | null
  inicio: string | null
  entregue_em: string | null
  /** 0 a 100, calculado do quadro na publicação; null sem demandas. */
  pct: number | null
  historico: HistoricoDePrazo[]
  demandas: DemandaPublicada[]
}

/** O que a equipe tirou da publicação nos últimos 30 dias, com o motivo (o banco já filtra). */
export type SaidaDoPlano = {
  alvo: 'bloco' | 'demanda'
  id: number
  titulo: string
  /** O bloco de onde a demanda saiu; nulo quando a saída é do próprio bloco. */
  bloco_titulo: string | null
  motivo: string
  em: string
}

export type ProgressoPublicado = {
  blocos: BlocoPublicado[]
  /** Antes da migration das saídas o banco não manda o campo: o SDK devolve []. */
  saidas: SaidaDoPlano[]
  publicado_em: string
}

export type TipoDePedido = 'acesso' | 'aprovacao' | 'material' | 'decisao'

export type AnexoDoPedido = { path: string; nome: string; tamanho?: number; tipo?: string }

export type PedidoAoCliente = {
  id: string
  block_id: number
  task_id: number | null
  tipo: TipoDePedido
  titulo: string
  descricao: string
  limite: string | null
  /** nada, prazo_bloco ou demanda. */
  trava: string
  trava_titulo: string | null
  /** aberta, respondida ou resolvida. */
  status: string
  pedido_por: string
  criado_em: string
  respondido_em: string | null
  resposta_texto: string | null
  resposta_feito: boolean
  resposta_anexos: AnexoDoPedido[]
  resolvido_em: string | null
}

// As funções nascem em 20261022200000_progresso_do_cliente_publicado.sql e só
// entram no database.types.ts quando o gen:types rodar depois de aplicada: até
// lá a chamada sem tipo fica num lugar só, como no chamado (ADR-085).
type RespostaRpc = { data: unknown; error: { code?: string; message: string } | null }

function rpcDoPortal(
  client: SupabaseClient,
  nome: string,
  args: Record<string, unknown>,
): Promise<RespostaRpc> {
  const portal = client.schema('portal') as unknown as {
    rpc: (nome: string, args: Record<string, unknown>) => Promise<RespostaRpc>
  }
  return portal.rpc(nome, args)
}

// A tela degrada, não quebra (régua do portal): função que ainda não existe no
// ambiente vira o estado vazio, e não tela branca.
function funcaoAusente(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false
  return (
    error.code === 'PGRST202' ||
    error.code === '42883' ||
    /could not find the function/i.test(error.message ?? '')
  )
}

/** A última publicação do Progresso. `null` quando a equipe ainda não publicou. */
export async function getProgressoPublicado(
  client: SupabaseClient,
  initiativeId: number,
): Promise<ProgressoPublicado | null> {
  const { data, error } = await rpcDoPortal(client, 'progresso_publicado', {
    p_initiative_id: initiativeId,
  })

  if (funcaoAusente(error)) return null
  if (error) throw new SupabaseError(error.message, error)
  if (!data) return null

  const p = data as {
    blocos?: BlocoPublicado[] | null
    saidas?: SaidaDoPlano[] | null
    publicado_em?: string
  }
  return {
    blocos: p.blocos ?? [],
    saidas: Array.isArray(p.saidas) ? p.saidas : [],
    publicado_em: String(p.publicado_em ?? ''),
  }
}

/** Os pedidos da equipe ao cliente nesta iniciativa, em todos os status. */
export async function listPedidosDaIniciativa(
  client: SupabaseClient,
  initiativeId: number,
): Promise<PedidoAoCliente[]> {
  const { data, error } = await rpcDoPortal(client, 'pedidos_da_iniciativa', {
    p_initiative_id: initiativeId,
  })

  if (funcaoAusente(error)) return []
  if (error) throw new SupabaseError(error.message, error)
  return ((data as PedidoAoCliente[] | null) ?? []).map((p) => ({
    ...p,
    resposta_anexos: Array.isArray(p.resposta_anexos) ? p.resposta_anexos : [],
  }))
}

/** O cliente responde o pedido (texto, anexos) ou avisa que já fez. Uma vez só. */
export async function responderPedido(
  client: SupabaseClient,
  pedidoId: string,
  resposta: { texto?: string | null; feito?: boolean; anexos?: AnexoDoPedido[] },
): Promise<void> {
  const { error } = await rpcDoPortal(client, 'responder_pedido', {
    p_pedido_id: pedidoId,
    p_texto: resposta.texto ?? null,
    p_feito: resposta.feito ?? false,
    p_anexos: resposta.anexos ?? [],
  })
  if (error) throw new SupabaseError(error.message, error)
}

const PEDIDOS_BUCKET = 'pedidos-cliente'

/** Sobe um anexo da resposta e devolve o registro que vai na resposta. */
export async function subirAnexoDoPedido(
  client: SupabaseClient,
  projectId: number,
  pedidoId: string,
  file: File,
): Promise<AnexoDoPedido> {
  const limpo = file.name.normalize('NFD').replace(/[^\w.-]+/g, '_')
  const path = `${projectId}/${pedidoId}/${Date.now()}_${limpo}`
  const { error } = await client.storage.from(PEDIDOS_BUCKET).upload(path, file)
  if (error) throw new SupabaseError(error.message, error)
  return { path, nome: file.name, tamanho: file.size, tipo: file.type }
}

/** Link temporário para abrir um anexo (o espaço é privado). */
export async function linkDoAnexoDoPedido(
  client: SupabaseClient,
  path: string,
  segundos = 3600,
): Promise<string | null> {
  const { data, error } = await client.storage.from(PEDIDOS_BUCKET).createSignedUrl(path, segundos)
  if (error) return null
  return data?.signedUrl ?? null
}
